import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { Transaction, TransactionType } from '../types';
interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (transaction: Omit<Transaction, 'id'> & {
    id?: string;
  }) => void;
  onDelete?: (id: string) => void;
  initialData?: Transaction | null;
  initialType?: TransactionType | null;
}
export function TransactionModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialData,
  initialType
}: TransactionModalProps) {
  const [type, setType] = useState<TransactionType>('expense');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isDebt, setIsDebt] = useState(false);
  const [debtPerson, setDebtPerson] = useState('');
  const [affectsBalance, setAffectsBalance] = useState(true);
  useEffect(() => {
    if (initialData) {
      setType(initialData.type);
      setName(initialData.name);
      setAmount(initialData.amount.toString());
      setCategory(initialData.category || '');
      setDate(initialData.date.split('T')[0]);
      setIsDebt(initialData.isDebt || false);
      setDebtPerson(initialData.debtPerson || '');
      setAffectsBalance(initialData.affectsBalance ?? true);
    } else {
      setType(initialType || 'expense');
      setName('');
      setAmount('');
      setCategory('');
      setDate(new Date().toISOString().split('T')[0]);
      setIsDebt(initialType === 'income' || initialType === 'expense');
      setDebtPerson('');
      setAffectsBalance(true);
    }
  }, [initialData, initialType, isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate category for expenses
    if (type === 'expense' && !category) {
      return;
    }
    onSave({
      id: initialData?.id,
      type,
      name,
      amount: parseFloat(amount),
      category: type === 'expense' ? category : type,
      date: new Date(date).toISOString(),
      isDebt,
      debtPerson: isDebt ? debtPerson : undefined,
      affectsBalance: isDebt ? affectsBalance : true
    });
    onClose();
  };
  const categories = ['Food', 'Transport', 'Shopping', 'Bills', 'Entertainment', 'Health', 'Parents', 'Essentials', 'School', 'Trip', 'Other'];
  return <AnimatePresence data-id="element-97">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-98" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-stone-900 rounded-t-3xl z-50 p-6 max-h-[90vh] overflow-y-auto border-t border-stone-800" data-id="element-99">
            <div className="flex justify-between items-center mb-6" data-id="element-100">
              <h2 className="text-2xl font-bold text-stone-50" data-id="element-101">
                {initialData ? 'Edit Transaction' : 'New Transaction'}
              </h2>
              <button onClick={onClose} className="p-2 bg-stone-800 rounded-full text-stone-400 hover:text-stone-200" data-id="element-102">
                <X size={20} data-id="element-103" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-104">
              {/* Type Selector */}
              <div className="flex bg-stone-800 p-1 rounded-xl" data-id="element-105">
                {(['expense', 'income', 'savings'] as const).map(t => <button key={t} type="button" onClick={() => setType(t)} className={`flex-1 py-3 text-sm font-medium capitalize rounded-lg transition-colors ${type === t ? t === 'expense' ? 'bg-red-500/20 text-red-500' : t === 'income' ? 'bg-amber-500/20 text-amber-500' : 'bg-emerald-500/20 text-emerald-500' : 'text-stone-400 hover:text-stone-200'}`} data-id="element-106">
                    {t}
                  </button>)}
              </div>

              {/* Amount Input */}
              <div data-id="element-107">
                <label className="block text-xs font-medium text-stone-500 uppercase mb-1" data-id="element-108">
                  Amount
                </label>
                <div className="relative" data-id="element-109">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 text-lg" data-id="element-110">
                    $
                  </span>
                  <input type="number" step="0.01" required value={amount} onChange={e => setAmount(e.target.value)} className="w-full bg-stone-800 text-stone-50 text-2xl font-bold py-4 pl-10 pr-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="0.00" data-id="element-111" />
                </div>
              </div>

              {/* Name Input */}
              <div data-id="element-112">
                <label className="block text-xs font-medium text-stone-500 uppercase mb-1" data-id="element-113">
                  Description
                </label>
                <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-stone-800 text-stone-50 py-4 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="What is this for?" data-id="element-114" />
              </div>

              {/* Category (Expense Only) */}
              {type === 'expense' && <div data-id="element-115">
                  <label className="block text-xs font-medium text-stone-500 uppercase mb-1" data-id="element-116">
                    Category *
                  </label>
                  <select value={category} onChange={e => setCategory(e.target.value)} required className="w-full bg-stone-800 text-stone-50 py-4 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 appearance-none" data-id="element-117">
                    <option value="" disabled data-id="element-118">
                      Select a category
                    </option>
                    {categories.map(c => <option key={c} value={c} data-id="element-119">
                        {c}
                      </option>)}
                  </select>
                </div>}

              {/* Date Picker */}
              <div data-id="element-120">
                <label className="block text-xs font-medium text-stone-500 uppercase mb-1" data-id="element-121">
                  Date
                </label>
                <input type="date" required value={date} onChange={e => setDate(e.target.value)} className="w-full bg-stone-800 text-stone-50 py-4 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" data-id="element-122" />
              </div>

              {/* Debt/Loan Options */}
              {(type === 'income' || type === 'expense') && <div className="bg-stone-800/50 p-4 rounded-2xl border border-stone-800" data-id="element-123">
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-124">
                    <input type="checkbox" checked={isDebt} onChange={e => setIsDebt(e.target.checked)} className="w-5 h-5 rounded-md border-stone-600 text-amber-500 focus:ring-amber-500 bg-stone-700" data-id="element-125" />
                    <span className="text-stone-300" data-id="element-126">
                      {type === 'income' ? 'Borrowed from someone' : 'Lent to someone'}
                    </span>
                  </label>

                  {isDebt && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} className="mt-4 space-y-4 overflow-hidden" data-id="element-127">
                      <input type="text" placeholder="Person's Name" value={debtPerson} onChange={e => setDebtPerson(e.target.value)} className="w-full bg-stone-700 text-stone-50 py-3 px-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" data-id="element-128" />

                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-129">
                        <input type="checkbox" checked={!affectsBalance} onChange={e => setAffectsBalance(!e.target.checked)} className="w-5 h-5 rounded-md border-stone-600 text-amber-500 focus:ring-amber-500 bg-stone-700" data-id="element-130" />
                        <span className="text-stone-400 text-sm" data-id="element-131">
                          Don't {type === 'income' ? 'add to' : 'deduct from'}{' '}
                          my balance
                        </span>
                      </label>
                    </motion.div>}
                </div>}

              <div className="flex gap-3 pt-4" data-id="element-132">
                {initialData && onDelete && <button type="button" onClick={() => {
              if (confirm('Are you sure you want to delete this transaction?')) {
                onDelete(initialData.id);
                onClose();
              }
            }} className="flex-1 py-4 bg-stone-800 text-red-500 font-bold rounded-2xl hover:bg-stone-700 transition-colors" data-id="element-133">
                    Delete
                  </button>}
                <button type="submit" className="flex-[2] py-4 bg-amber-500 text-stone-900 font-bold rounded-2xl hover:bg-amber-400 transition-colors flex items-center justify-center gap-2" data-id="element-134">
                  <Check size={20} data-id="element-135" />
                  Save Transaction
                </button>
              </div>
            </form>
          </motion.div>
        </>}
    </AnimatePresence>;
}